import Seo from '@/components/feature/Seo';
import Breadcrumbs from '@/components/feature/Breadcrumbs';
import CategoryHeader from '@/pages/category/components/CategoryHeader';
import CategorySidebar from '@/pages/category/components/CategorySidebar';
import CategoryToolbar from '@/pages/category/components/CategoryToolbar';
import ArticleCard from '@/components/feature/ArticleCard';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { useMemo, useState } from 'react';
import { articles, categories, getCategoryBySlug } from '@/mocks/gossip';

type SortKey = 'latest' | 'hot';

export default function CategoryPage() {
  const { slug = 'all' } = useParams();
  const [searchParams] = useSearchParams();
  const [activeTag, setActiveTag] = useState('全部');
  const [sortKey, setSortKey] = useState<SortKey>('latest');

  const currentCategory = slug === 'all' ? null : getCategoryBySlug(slug) || null;
  const categoryName = currentCategory ? currentCategory.name : '全部图集';
  const keyword = (searchParams.get('q') || '').trim();

  const baseList = useMemo(
    () => (currentCategory ? articles.filter((item) => item.category === currentCategory.slug) : articles),
    [currentCategory],
  );

  const tags = useMemo(() => {
    const set = new Set<string>();
    baseList.forEach((item) => item.tags.forEach((tag) => set.add(tag)));
    return ['全部', ...Array.from(set)];
  }, [baseList]);

  const list = useMemo(() => {
    let result = baseList.filter((item) => activeTag === '全部' || item.tags.includes(activeTag));
    if (keyword) {
      const lower = keyword.toLowerCase();
      result = result.filter(
        (item) =>
          item.title.toLowerCase().includes(lower) ||
          item.excerpt.toLowerCase().includes(lower) ||
          item.tags.some((tag) => tag.toLowerCase().includes(lower)),
      );
    }
    return [...result].sort((a, b) =>
      sortKey === 'latest' ? b.date.localeCompare(a.date) : a.views.localeCompare(b.views),
    );
  }, [baseList, activeTag, sortKey, keyword]);

  const notFound = slug !== 'all' && !currentCategory;

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${categoryName} - 诱惑吃瓜`,
    description: currentCategory
      ? currentCategory.intro
      : '诱惑吃瓜全部图集，聚合颜值美照、生图红毯、高光同框与造型解析内容。',
    inLanguage: 'zh-CN',
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: list.slice(0, 10).map((item, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: item.title,
        url: `${window.location.origin}/article/${item.slug}`,
      })),
    },
  };

  return (
    <>
      <Seo
        title={`${categoryName} - 诱惑吃瓜 | 高清图集与红毯名场面`}
        description={
          currentCategory
            ? `${currentCategory.intro} 浏览诱惑吃瓜${categoryName}专栏的高清图集，看生图细节与造型亮点。`
            : '诱惑吃瓜全部图集，聚合颜值美照、生图红毯、高光同框与造型解析，高清美图与红毯名场面一次看够。'
        }
        keywords={`${categoryName},诱惑吃瓜,${categoryName}图集,颜值美照,生图红毯,高光同框`}
        type="website"
        robots={notFound ? 'noindex, follow' : 'index, follow'}
        jsonLd={structuredData}
      />

      <Breadcrumbs items={[{ label: '首页', path: '/' }, { label: categoryName }]} />

      <CategoryHeader
        name={categoryName}
        intro={
          currentCategory
            ? currentCategory.intro
            : '按更新时间聚合全部图集，颜值美照、生图红毯、高光同框与造型解析都在这里。'
        }
        image={currentCategory ? currentCategory.image : categories[0].image}
        imageAlt={currentCategory ? currentCategory.alt : '诱惑吃瓜全部图集封面'}
        count={list.length}
      />

      <div className="mx-auto max-w-[1240px] px-4 py-12 md:px-8 md:py-16">
        {notFound ? (
          <div className="rounded-lg border border-background-200 bg-background-100 px-6 py-16 text-center">
            <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-background-200 text-2xl text-foreground-600">
              <i className="ri-error-warning-line" aria-hidden="true"></i>
            </span>
            <h2 className="mt-4 font-heading text-xl font-bold text-foreground-950">没有找到这个栏目</h2>
            <p className="mt-2 text-sm text-foreground-600">它可能已经调整位置，或者链接输入有误。</p>
            <Link
              to="/category/all"
              className="mt-6 inline-flex h-10 items-center gap-1 rounded-md bg-primary-500 px-5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
            >
              浏览全部图集
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
            <div className="lg:col-span-8">
              <CategoryToolbar
                tags={tags}
                activeTag={activeTag}
                onTagChange={setActiveTag}
                sortKey={sortKey}
                onSortChange={setSortKey}
                keyword={keyword}
              />

              {list.length === 0 ? (
                <div className="mt-8 rounded-lg border border-background-200 bg-background-100 px-6 py-16 text-center">
                  <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-background-200 text-2xl text-foreground-600">
                    <i className="ri-search-eye-line" aria-hidden="true"></i>
                  </span>
                  <h2 className="mt-4 font-heading text-lg font-bold text-foreground-950">
                    没有匹配的内容
                  </h2>
                  <p className="mt-2 text-sm text-foreground-600">换一个标签或关键词再试试。</p>
                </div>
              ) : (
                <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2">
                  {list.map((article) => (
                    <ArticleCard key={article.id} article={article} />
                  ))}
                </div>
              )}
            </div>

            <aside className="lg:col-span-4">
              <CategorySidebar />
            </aside>
          </div>
        )}
      </div>
    </>
  );
}